import type { AnchoredRow } from '../types/anchoredRowTypes'
import type { CompleteMicroSegmentationResult } from '../types/segmentationTypes'
import {
  buildLedgerSafeMappingReferenceIndex,
  buildLedgerSafeResolvedMappedUnitUuidSet,
  getLedgerSafeActiveMapping,
  resolveLedgerSafeMappingToOrderedIndex
} from './ledgerSafeBoundaryMapping'
import {
  getOrderedUnitUuid,
  getOrderedWitnessUnitStorage,
  getOrderedWitnessUnits,
  resolveLedgerSafeSegmentationSourceText
} from './ledgerSafeBoundarySource'
import {
  cloneLedgerSafeSegmentationEnvelope,
  getLedgerSafeStorageContainerStart,
  getLedgerSafeStoredUnit,
  refreshLedgerSafeTotalUnitCount,
  removeLedgerSafeStoredUnits,
  removeLedgerSafeUnitIdentitiesFromNumbers
} from './ledgerSafeWitnessUnitStream'
import {
  stripFoliationMarkers
} from './manualStudioSegmentationResult'

export interface LedgerSafeUnitMergeSegmentationApplyResult {
  updatedSegmentationResult: CompleteMicroSegmentationResult
  changedUnitUuids: string[]
  survivorUnitUuid: string
  absorbedUnitUuids: string[]
  mergedText: string
}

export const getLedgerSafeContiguousWindowRows = (rows: AnchoredRow[], rowIds: string[]): AnchoredRow[] => {
  const uniqueRowIds = Array.from(new Set(rowIds))
  if (uniqueRowIds.length < 2) {
    throw new Error('Select at least two rows to merge')
  }
  const indexes = uniqueRowIds.map(rowId => rows.findIndex(row => row.id === rowId))
  if (indexes.some(index => index < 0)) {
    throw new Error('One or more selected rows were not found')
  }
  const sortedIndexes = [...indexes].sort((left, right) => left - right)
  for (let offset = 1; offset < sortedIndexes.length; offset += 1) {
    if (sortedIndexes[offset] !== sortedIndexes[0] + offset) {
      throw new Error('Merge requires a contiguous row window')
    }
  }
  return rows.slice(sortedIndexes[0], sortedIndexes[sortedIndexes.length - 1] + 1)
}

export const applyLedgerSafeMergeUnitsToSegmentation = ({
  rows,
  witnessId,
  rowIds,
  segmentationResult,
  sourceText
}: {
  rows: AnchoredRow[]
  witnessId: string
  rowIds: string[]
  segmentationResult: CompleteMicroSegmentationResult | null | undefined
  sourceText: string
}): LedgerSafeUnitMergeSegmentationApplyResult => {
  if (!segmentationResult || !sourceText) {
    throw new Error('Selected units cannot be merged')
  }

  const effectiveSourceText = resolveLedgerSafeSegmentationSourceText({
    segmentationResult,
    fallbackSourceText: sourceText
  })
  const windowRows = getLedgerSafeContiguousWindowRows(rows, rowIds)
  const mappedWindowRows = windowRows.filter(row => Boolean(getLedgerSafeActiveMapping(row, witnessId)?.unitUuid))
  if (mappedWindowRows.length < 2) {
    throw new Error('Select at least two mapped units to merge')
  }

  const orderedUnits = getOrderedWitnessUnits(segmentationResult)
  const orderedIndexByReference = buildLedgerSafeMappingReferenceIndex({
    orderedUnits,
    idRemap: segmentationResult.id_remap
  })
  const claimedOrderedIndexes = new Set<number>()
  const selectedUnits = mappedWindowRows.map(row => {
    const mapping = getLedgerSafeActiveMapping(row, witnessId)
    const resolved = resolveLedgerSafeMappingToOrderedIndex({
      mapping,
      orderedUnits,
      orderedIndexByReference,
      claimedOrderedIndexes
    })
    const orderedIndex = resolved?.orderedIndex
    const entry = typeof orderedIndex === 'number' ? orderedUnits[orderedIndex] : undefined
    if (!mapping || typeof orderedIndex !== 'number' || !entry) {
      throw new Error('Unable to locate selected unit in segmentation result')
    }
    claimedOrderedIndexes.add(orderedIndex)
    return { entry, orderedIndex }
  })

  const hasInvalidSelectedSpan = selectedUnits.some(({ entry }) => {
    const start = Number(entry.unit.start_position)
    const end = Number(entry.unit.end_position)
    return !Number.isFinite(start) || !Number.isFinite(end) || end <= start
  })
  if (hasInvalidSelectedSpan) {
    throw new Error('Unable to compute merged unit span')
  }

  const mappedUnitUuidSet = buildLedgerSafeResolvedMappedUnitUuidSet({
    rows,
    witnessId,
    orderedUnits,
    idRemap: segmentationResult.id_remap
  })
  for (let offset = 1; offset < selectedUnits.length; offset += 1) {
    const previousSelectedIndex = selectedUnits[offset - 1].orderedIndex
    const nextSelectedIndex = selectedUnits[offset].orderedIndex
    if (nextSelectedIndex !== previousSelectedIndex + 1) {
      if (nextSelectedIndex <= previousSelectedIndex) {
        throw new Error('Merge requires selected rows to follow witness source order')
      }
      const interveningEntries = orderedUnits.slice(previousSelectedIndex + 1, nextSelectedIndex)
      const unassignedSplitFragments = interveningEntries.filter(entry => {
        const unitUuid = getOrderedUnitUuid(entry.unit)
        return Boolean(unitUuid) &&
          !mappedUnitUuidSet.has(unitUuid) &&
          Boolean(entry.unit.processing_metadata?.ledger_safe_split_from)
      })
      if (unassignedSplitFragments.length > 0) {
        throw new Error('Merge blocked: this witness has an unassigned split fragment between the selected rows. Place the fragment before merging.')
      }

      const unassignedInterveningUnits = interveningEntries.filter(entry => {
        const unitUuid = getOrderedUnitUuid(entry.unit)
        return Boolean(unitUuid) && !mappedUnitUuidSet.has(unitUuid)
      })
      if (unassignedInterveningUnits.length > 0) {
        throw new Error('Merge blocked: this witness has an unassigned intervening unit between the selected rows. Place it before merging.')
      }

      throw new Error('Merge requires adjacent units in segmentation order')
    }
  }

  const selectedEntries = selectedUnits.map(selected => selected.entry)
  const survivorEntry = selectedEntries[0]
  const absorbedEntries = selectedEntries.slice(1)
  const survivorUnitUuid = String(survivorEntry.unit.unit_uuid || survivorEntry.unit.unit_id || '')
  const absorbedUnitUuids = absorbedEntries.map(entry => String(entry.unit.unit_uuid || entry.unit.unit_id || ''))
  const mergedStart = Number(survivorEntry.unit.start_position)
  const mergedEnd = Number(selectedEntries[selectedEntries.length - 1].unit.end_position)
  if (!Number.isFinite(mergedStart) || !Number.isFinite(mergedEnd) || mergedEnd <= mergedStart) {
    throw new Error('Unable to compute merged unit span')
  }

  const mergedText = stripFoliationMarkers(effectiveSourceText.slice(mergedStart, mergedEnd))
  if (!mergedText.trim()) {
    throw new Error('Merged unit cannot be empty')
  }

  const updatedSegmentationResult = cloneLedgerSafeSegmentationEnvelope(segmentationResult)
  const survivorStorage = getOrderedWitnessUnitStorage(survivorEntry)
  const survivorUnit = getLedgerSafeStoredUnit(updatedSegmentationResult, survivorStorage)
  if (!survivorUnit) {
    throw new Error('Unable to locate survivor unit in segmentation result')
  }

  const containerStart = getLedgerSafeStorageContainerStart(updatedSegmentationResult, survivorStorage)
  const timestamp = new Date().toISOString()
  survivorUnit.end_position = mergedEnd
  survivorUnit.chunk_relative_end = mergedEnd - containerStart
  survivorUnit.unit_length_chars = mergedEnd - mergedStart
  survivorUnit.unit_text_content = mergedText
  survivorUnit.processing_metadata = {
    ...(survivorUnit.processing_metadata || {}),
    ledger_safe_merge: {
      merged_at: timestamp,
      source_unit_uuids: [survivorUnitUuid, ...absorbedUnitUuids],
      absorbed_unit_uuids: absorbedUnitUuids
    }
  }

  removeLedgerSafeStoredUnits(
    updatedSegmentationResult,
    absorbedEntries.map(getOrderedWitnessUnitStorage)
  )
  removeLedgerSafeUnitIdentitiesFromNumbers(updatedSegmentationResult, absorbedUnitUuids)
  refreshLedgerSafeTotalUnitCount(updatedSegmentationResult)

  updatedSegmentationResult.segmentation_metadata = {
    ...(updatedSegmentationResult.segmentation_metadata || {}),
    timestamp
  }
  updatedSegmentationResult.id_remap = {
    ...(updatedSegmentationResult.id_remap || {}),
    ...absorbedUnitUuids.reduce<Record<string, string[]>>((accumulator, unitUuid) => {
      accumulator[unitUuid] = [survivorUnitUuid]
      return accumulator
    }, {})
  }

  return {
    updatedSegmentationResult,
    changedUnitUuids: [survivorUnitUuid, ...absorbedUnitUuids],
    survivorUnitUuid,
    absorbedUnitUuids,
    mergedText
  }
}
